import { EventEmitter, Injectable } from '@angular/core';
import { isKeyValEntryUnkownData, KeyValEntry } from '@shared/KeyValStore';
import { waitSeconds } from '@reflact/tsutil';
import dayjs from 'dayjs';
import { z } from 'zod';
import { EventManagerAdminService } from './event-manager/event-manager-admin.service';
import { SocketService } from './socket-admin.service';

@Injectable({
  providedIn: 'root'
})
export class KeyValAdminService {
  public onKeyValUpdate: EventEmitter<KeyValEntry> = new EventEmitter<KeyValEntry>();
  public onKeyValDelete: EventEmitter<KeyValEntry> = new EventEmitter<KeyValEntry>();

  public keyValMap = new Map<string, KeyValEntry>();

  public initDone = false;

  constructor(
    private socketService: SocketService,
    private eventManager: EventManagerAdminService
  ) {
    void this.init();
  }

  private getMapKey(key: string, type: string) {
    return `${type}-${key}`;
  }
  private getMapKeyByEntry(entry: KeyValEntry) {
    return this.getMapKey(entry.key, entry.type);
  }

  public async init() {
    const io = await this.socketService.getIo();
    io.on('keyValUpdate', (entry: KeyValEntry) => {
      this.onKeyValUpdate.emit(entry);
      if (isKeyValEntryUnkownData(entry)) {
        this.keyValMap.set(this.getMapKeyByEntry(entry), entry);
      }
    });
    io.on('keyValDelete', (entry: KeyValEntry) => {
      this.onKeyValDelete.emit(entry);
      if (isKeyValEntryUnkownData(entry)) {
        this.keyValMap.delete(this.getMapKeyByEntry(entry));
      }
    });
    const items = await this.getInitialValues();
    for (const item of items) {
      if (isKeyValEntryUnkownData(item)) {
        this.keyValMap.set(this.getMapKeyByEntry(item), item);
      }
    }
    this.initDone = true;
  }

  public async waitForInit() {
    if (this.initDone) {
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.KEYVAL.waitForInit", 'KeyVal Init done - keyval-admin service');
      return;
    }
    await waitSeconds(0.1);
    await this.waitForInit();
    return null;
  }

  public async getInitialValues() {
    const io = await this.socketService.getIo();
    return new Promise<KeyValEntry[]>((resolve, _reject) => {
      io.emit('getInitialKeyVals', (entries: KeyValEntry[]) => {
        resolve(entries);
      });
    });
  }

  public async deleteKeyValEntry(key: string | z.input<typeof KeyValAdminService.keyValGetSchema>, type: string = "custom"): Promise<KeyValEntry> {
    const parsed = KeyValAdminService.keyValGetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      return await this.deleteKeyValEntry(parsed.data.key, parsed.data.type);
    }
    await this.waitForInit();
    const io = await this.socketService.getIo();
    return new Promise((resolve) => {
      io.emit('removeKeyVal', { key: key as string, type }, (data: KeyValEntry) => {
        this.keyValMap.delete(this.getMapKey(key as string, type));
        this.eventManager._trigger_RAGAI_EVENT("RAGAI.KEYVAL.deleteKeyValEntry", data);
        resolve(data);
      });
    });
  }

  public async storeKeyValEntry(key: string | z.input<typeof KeyValAdminService.keyValObjectSchema>, value?: unknown, type: string = "custom"): Promise<KeyValEntry> {
    const parsed = KeyValAdminService.keyValObjectSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      return await this.storeKeyValEntry(parsed.data.key, parsed.data.value, parsed.data.type);
    }
    await this.waitForInit();
    const io = await this.socketService.getIo();
    return new Promise((resolve) => {
      io.emit('storeKeyValUpdate', { key: key as string, value: value, type }, (data: KeyValEntry) => {
        this.keyValMap.set(this.getMapKey(key as string, type), data);
        this.eventManager._trigger_RAGAI_EVENT("RAGAI.KEYVAL.storeKeyValEntry", data);
        resolve(data);
      });
    });
  }

  public async getKeyValEntry(key: string | z.input<typeof KeyValAdminService.keyValGetSchema>, type: string = "custom"): Promise<unknown> {
    const parsed = KeyValAdminService.keyValGetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      return await this.getKeyValEntry(parsed.data.key, parsed.data.type);
    }
    await this.waitForInit();
    const found = this.keyValMap.get(this.getMapKey(key as string, type));
    if (found) {
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.KEYVAL.getKeyValEntry", { key, value: found.value });
      return found.value;
    } else {
      return undefined;
    }
  }

  public async getKeyValByType(type: string) {
    await this.waitForInit();
    const results: KeyValEntry[] = [];
    this.keyValMap.forEach((val, _key) => {
      if (val.type === type) {
        results.push(val);
      }
    });
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.KEYVAL.getKeyValByType", results);
    return results;
  }

  private static readonly keyValGetSchema = z.object({
    key: z.string(),
    type: z.string().optional().default("custom")
  });

  public async get(key: string | z.input<typeof KeyValAdminService.keyValGetSchema>, type: string = "custom"): Promise<unknown> {
    const parsed = KeyValAdminService.keyValGetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      return await this.get(parsed.data.key, parsed.data.type);
    }
    const val = (await this.getByType(type))
      .find(obj => obj.key === key)
      ?.value ?? null;
    return val;
  }


  private static readonly keyValObjectSchema = z.object({
    key: z.string(),
    value: z.unknown(),
    type: z.string().optional().default("custom")
  });
  public async set(key: string | z.input<typeof KeyValAdminService.keyValObjectSchema>, value?: unknown, type: string = "custom"): Promise<KeyValEntry> {
    const parsed = KeyValAdminService.keyValObjectSchema.safeParse(key);
    if (parsed.success && value === undefined) {
      return await this.set(parsed.data.key, parsed.data.value, parsed.data.type);
    }

    return await this.storeKeyValEntry(key, value, type);

  }





  public async unique(key: string | z.input<typeof KeyValAdminService.keyValObjectSchema>, value?: unknown, type: string = "custom"): Promise<KeyValEntry> {
    const parsed = KeyValAdminService.keyValObjectSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      return await this.unique(parsed.data.key, parsed.data.value, parsed.data.type);
    }
    return await this.storeKeyValEntry((key as string) + '_' + dayjs().toISOString(), value, type);
  }
  public async getByType(type: string) { return this.getKeyValByType(type); }

}
